import React from 'react';
import { useFormContext } from 'react-hook-form';
import strings from 'strings';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import { BULK_RESTRICTION_RULE } from 'UI/constants/defaults';

/**
 * @typedef {Object} RestrictionBulk
 * @property {string} rule
 * @property {string} reason
 */

const { restrictions: restrictionsCopies } = strings.bulkEmails.dialogs;

const MAPPED_RESTRICTION_TITLES = {
  defaultRestriction: restrictionsCopies.titles.defaultRestriction,
  noQuotaAvailable: restrictionsCopies.titles.noQuotaAvailable,
  quotaNearlyReached: restrictionsCopies.titles.quotaNearlyReached,
  scheduleCloseNearScheduled: restrictionsCopies.titles.schedule,
  scheduleCloseNearSent: restrictionsCopies.titles.schedule,
  sendNearSent: restrictionsCopies.titles.send,
  sendNearScheduled: restrictionsCopies.titles.send
};

/**
 * @param {Object} props
 * @param {RestrictionBulk} props.bulkRestriction
 * @param {boolean} [props.isSendScheduleNow]
 * @param {Function} props.onClose
 * @param {Function} props.onOpenScheduleDialog
 * @param {Function} [props.onSaveAsDraft]
 * @param {Function} [props.onSubmitBulkEmail]
 * @param {boolean} [props.shouldComposeBulkSequence]
 */
const RestrictionDialog = ({
  bulkRestriction,
  isSendScheduleNow,
  onClose,
  onOpenScheduleDialog,
  onSaveAsDraft,
  onSubmitBulkEmail,
  shouldComposeBulkSequence
}) => {
  const handleOnClose = () => onClose && onClose();

  const form = useFormContext();

  const restrictionActions = {
    getCancelButton: (text, variant = 'outlined') => ({
      variant,
      text,
      onClick: handleOnClose,
      show: true
    }),
    getScheduleButton: text => ({
      variant: 'contained',
      text,
      onClick: onOpenScheduleDialog,
      show: true
    }),
    getContinueButton: text => ({
      variant: 'contained',
      text,
      onClick: () => {
        if (isSendScheduleNow) {
          onSubmitBulkEmail();
          return;
        }
        const formValues = form.getValues({ nest: true });
        handleOnClose();
        onSubmitBulkEmail(formValues);
      },
      show: true
    }),
    getSaveAsDraftButton: (shouldShow = true) => ({
      variant: 'outlined',
      text: restrictionsCopies.actions.saveAsDraft,
      onClick: onSaveAsDraft,
      show: shouldShow && !shouldComposeBulkSequence
    })
  };
  const {
    close: closeCopy,
    cancel: cancelCopy,
    schedule: scheduleCopy,
    setDifferentTime: setDifferentTimeCopy,
    continueAnyway: continueAnywayCopy
  } = restrictionsCopies.actions;
  const mappedActionsByRestrictionRule = {
    [BULK_RESTRICTION_RULE.defaultRestriction]: [
      restrictionActions.getCancelButton(closeCopy, 'contained')
    ],
    [BULK_RESTRICTION_RULE.noQuotaAvailable]: [
      restrictionActions.getCancelButton(cancelCopy),
      restrictionActions.getSaveAsDraftButton(!isSendScheduleNow),
      restrictionActions.getScheduleButton(scheduleCopy)
    ],
    [BULK_RESTRICTION_RULE.quotaNearlyReached]: [
      restrictionActions.getCancelButton(cancelCopy),
      restrictionActions.getSaveAsDraftButton(!isSendScheduleNow),
      restrictionActions.getContinueButton(continueAnywayCopy)
    ],
    [BULK_RESTRICTION_RULE.sendNearSent]: [
      isSendScheduleNow
        ? restrictionActions.getCancelButton(cancelCopy)
        : restrictionActions.getSaveAsDraftButton(),
      restrictionActions.getScheduleButton(isSendScheduleNow ? setDifferentTimeCopy : scheduleCopy)
    ],
    [BULK_RESTRICTION_RULE.sendNearScheduled]: [
      restrictionActions.getSaveAsDraftButton(),
      restrictionActions.getScheduleButton(scheduleCopy)
    ],
    [BULK_RESTRICTION_RULE.scheduleCloseNearSent]: [
      restrictionActions.getCancelButton(cancelCopy),
      restrictionActions.getScheduleButton(setDifferentTimeCopy)
    ],
    [BULK_RESTRICTION_RULE.scheduleCloseNearScheduled]: [
      restrictionActions.getCancelButton(cancelCopy),
      restrictionActions.getScheduleButton(setDifferentTimeCopy)
    ]
  };

  return (
    <DecisionDialog
      title={MAPPED_RESTRICTION_TITLES[bulkRestriction.rule]}
      message={bulkRestriction.reason}
      isOpened
      actions={mappedActionsByRestrictionRule[bulkRestriction.rule]}
      onClose={handleOnClose}
      severity="warning"
      showSeverityIcon
    />
  );
};

RestrictionDialog.defaultProps = {
  onSaveAsDraft: () => {},
  isSendScheduleNow: false
};

export default RestrictionDialog;
